import React, { useEffect, useState } from 'react';
import { createCompany, updateCompany } from '../../api/companies';
import { apiClient } from '../../api/client';
import { Company } from '../../types';
import { COUNTRIES as COUNTRY_LIST } from '../../lib/countries';

interface InvestorType { id: string; code: string; label_de: string; sort_order: number; }

interface CompanyFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  initial?: Company;  // wenn gesetzt: Bearbeiten-Modus
}

const COMPANY_TYPES = ['GmbH', 'AG', 'KG', 'GbR', 'LP', 'LLC', 'Ltd', 'REIT', 'Family Office', 'Fonds', 'Sonstige'];
const COUNTRIES = COUNTRY_LIST;

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--border2)',
  borderRadius: 'var(--radius)',
  fontSize: '13px',
  background: 'var(--surface)',
  color: 'var(--text)',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'var(--text3)',
  marginBottom: '5px',
};

export function CompanyForm({ onSuccess, onCancel, initial }: CompanyFormProps) {
  const isEdit = !!initial;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [researching, setResearching] = useState(false);
  const [researchMsg, setResearchMsg] = useState<string | null>(null);
  const [investorTypes, setInvestorTypes] = useState<InvestorType[]>([]);
  useEffect(() => {
    apiClient.get<{ data: InvestorType[] }>('/investor-types')
      .then(r => setInvestorTypes(r.data.data ?? [])).catch(() => {});
  }, []);

  const [form, setForm] = useState({
    display_name: initial?.display_name ?? '',
    legal_name: initial?.legal_name ?? '',
    company_type: initial?.company_type ?? '',
    investor_type_id: initial?.investor_type_id ?? '',
    // Strip protocol for display — we add it back on save
    website: (initial?.website ?? '').replace(/^https?:\/\//i, ''),
    street: initial?.street ?? '',
    postal_code: initial?.postal_code ?? '',
    city: initial?.city ?? '',
    country_code: initial?.country_code ?? 'DE',
    description: initial?.description ?? '',
    propstack_tags: initial?.propstack_tags ?? '',
    logo_url: initial?.logo_url ?? '',
  });

  const set = (field: string, value: string | boolean) =>
    setForm(f => ({ ...f, [field]: value }));

  const handleResearch = async () => {
    const website = form.website.trim();
    const name = form.display_name.trim();
    if (!website && !name) { setResearchMsg('Bitte zuerst Website oder Firmenname eingeben.'); return; }
    setResearching(true);
    setResearchMsg(null);
    try {
      const url = website
        ? (/^https?:\/\//i.test(website) ? website : `https://${website}`)
        : `https://www.google.com/search?q=${encodeURIComponent(name + ' Immobilien Adresse')}`;
      const { data } = await apiClient.post('/companies/research-web', {
        company_id: initial?.id,
        website: url,
        display_name: name,
      });
      // Felder automatisch befüllen mit den gefundenen Daten
      if (data.legal_name)    setForm(f => ({ ...f, legal_name: data.legal_name ?? f.legal_name }));
      if (data.company_type)  setForm(f => ({ ...f, company_type: data.company_type ?? f.company_type }));
      if (data.street)        setForm(f => ({ ...f, street: data.street ?? f.street }));
      if (data.postal_code)   setForm(f => ({ ...f, postal_code: data.postal_code ?? f.postal_code }));
      if (data.city)          setForm(f => ({ ...f, city: data.city ?? f.city }));
      if (data.country_code)  setForm(f => ({ ...f, country_code: data.country_code ?? f.country_code }));
      if (data.description)   setForm(f => ({ ...f, description: data.description ?? f.description }));
      if (data.logo_url)      setForm(f => ({ ...f, logo_url: data.logo_url }));
      setResearchMsg(`✓ Daten aus Internet geladen${data.source ? ` (${data.source})` : ''}`);
    } catch (err: unknown) {
      const apiErr = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setResearchMsg(apiErr ?? 'Keine Daten gefunden. Bitte manuell ausfüllen.');
    } finally {
      setResearching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.display_name.trim()) { setError('Firmenname ist Pflichtfeld'); return; }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        display_name: form.display_name.trim(),
        legal_name: form.legal_name.trim() || undefined,
        company_type: form.company_type || undefined,
        investor_type_id: form.investor_type_id || null,
        website: (() => { const w = form.website.trim(); if (!w) return undefined; return /^https?:\/\//i.test(w) ? w : `https://${w}`; })(),
        street: form.street.trim() || undefined,
        postal_code: form.postal_code.trim() || undefined,
        city: form.city.trim() || undefined,
        country_code: form.country_code || undefined,
        description: form.description.trim() || undefined,
        is_buyer: true,
        propstack_tags: form.propstack_tags.trim() || undefined,
        logo_url: form.logo_url || undefined,
      };
      if (isEdit && initial) {
        await updateCompany(initial.id, payload);
      } else {
        await createCompany(payload);
      }
      onSuccess();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div style={{ display: 'grid', gap: '14px' }}>

        {/* Firmenname + Logo */}
        <div>
          <label style={labelStyle}>Investorenname *</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {form.logo_url && (
              <img
                src={form.logo_url}
                alt=""
                style={{ width: 28, height: 28, borderRadius: 4, flexShrink: 0, objectFit: 'contain' }}
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <input
              style={{ ...inputStyle, flex: 1 }}
              value={form.display_name}
              onChange={e => set('display_name', e.target.value)}
              placeholder="z.B. Meridian Hotel Investments"
              required
              autoFocus
            />
          </div>
        </div>

        {/* Investorenart (Pflicht) */}
        <div>
          <label style={labelStyle}>Investorenart *</label>
          <select
            style={inputStyle}
            value={form.investor_type_id}
            onChange={e => set('investor_type_id', e.target.value)}
            required
          >
            <option value="">— wählen —</option>
            {investorTypes.map(t => <option key={t.id} value={t.id}>{t.label_de}</option>)}
          </select>
        </div>

        {/* Legal name + Rechtsform */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Vollständiger Rechtsname</label>
            <input
              style={inputStyle}
              value={form.legal_name}
              onChange={e => set('legal_name', e.target.value)}
              placeholder="z.B. Meridian Hotel Investments GmbH"
            />
          </div>
          <div>
            <label style={labelStyle}>Rechtsform</label>
            <select
              style={inputStyle}
              value={form.company_type}
              onChange={e => set('company_type', e.target.value)}
            >
              <option value="">— wählen —</option>
              {COMPANY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {/* Website + Recherche-Button nebeneinander */}
        <div>
          <label style={labelStyle}>Website</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
            <input
              style={{ ...inputStyle, flex: 1, width: 'auto', minWidth: 0 }}
              value={form.website}
              onChange={e => set('website', e.target.value)}
              placeholder="deka-immobilien.de"
              type="text"
            />
            <button
              type="button"
              onClick={handleResearch}
              disabled={researching}
              title="Adresse, Beschreibung und weitere Daten aus dem Internet laden"
              style={{
                padding: '8px 14px',
                background: researching ? 'var(--surface2)' : '#e8f5e9',
                border: '1px solid',
                borderColor: researching ? 'var(--border2)' : '#a5d6a7',
                borderRadius: 'var(--radius)',
                fontSize: '12px',
                fontWeight: 500,
                cursor: researching ? 'not-allowed' : 'pointer',
                color: researching ? 'var(--text3)' : '#2e7d32',
                display: 'flex', alignItems: 'center', gap: '5px',
                whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              {researching ? '⏳' : '🌐'} {researching ? 'Lädt...' : 'Aus Internet laden'}
            </button>
          </div>
          {researchMsg && (
            <div style={{
              marginTop: '5px', fontSize: '12px', padding: '4px 8px', borderRadius: 4,
              color: researchMsg.startsWith('✓') ? '#2e7d32' : 'var(--accent)',
              background: researchMsg.startsWith('✓') ? '#e8f5e9' : 'var(--accent-light)',
            }}>
              {researchMsg}
            </div>
          )}
        </div>

        {/* Straße */}
        <div>
          <label style={labelStyle}>Straße und Hausnummer</label>
          <input
            style={inputStyle}
            value={form.street}
            onChange={e => set('street', e.target.value)}
            placeholder="z.B. Schmidbauer Straße 4"
          />
        </div>

        {/* PLZ + Stadt + Land */}
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: '12px' }}>
          <div>
            <label style={labelStyle}>PLZ</label>
            <input
              style={inputStyle}
              value={form.postal_code}
              onChange={e => set('postal_code', e.target.value)}
              placeholder="z.B. 60329"
            />
          </div>
          <div>
            <label style={labelStyle}>Stadt</label>
            <input
              style={inputStyle}
              value={form.city}
              onChange={e => set('city', e.target.value)}
              placeholder="z.B. Frankfurt am Main"
            />
          </div>
          <div>
            <label style={labelStyle}>Land</label>
            <select
              style={inputStyle}
              value={form.country_code}
              onChange={e => set('country_code', e.target.value)}
            >
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
        </div>

        {/* Beschreibung */}
        <div>
          <label style={labelStyle}>Beschreibung</label>
          <textarea
            style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            value={form.description}
            onChange={e => set('description', e.target.value)}
            placeholder="z.B. Investmentmanager mit Fokus auf Gewerbeimmobilien..."
          />
        </div>

        {/* Tags */}
        <div>
          <label style={labelStyle}>Tags (kommagetrennt)</label>
          <input
            style={inputStyle}
            value={form.propstack_tags}
            onChange={e => set('propstack_tags', e.target.value)}
            placeholder="z.B. Kaufinteressent,Hotel,ab 10 Mio. €"
          />
        </div>

        {error && (
          <div style={{
            padding: '10px 14px',
            background: 'var(--accent-light)',
            color: 'var(--accent)',
            borderRadius: 'var(--radius)',
            fontSize: '13px',
          }}>
            {error}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '4px' }}>
          <button type="button" className="btn" onClick={onCancel} disabled={loading}>
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '8px 20px',
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--radius)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Wird gespeichert...' : isEdit ? 'Änderungen speichern' : 'Investor anlegen'}
          </button>
        </div>
      </div>
    </form>
  );
}
